import React, { useState, useEffect } from 'react';
import { Modal, Table, Spin, message, Typography, Divider } from 'antd';
import { Column } from '@ant-design/plots';
import apiClient from '../services/api';
import './DataAnalysisModal.css';

const { Title, Text } = Typography;

const DataAnalysisModal = ({ visible, onClose, taskId }) => {
  const [loading, setLoading] = useState(false);
  const [analysisData, setAnalysisData] = useState(null);

  const fetchAndAnalyzeData = async () => {
    setLoading(true);
    try {
      // 获取完整的Excel数据
      const response = await apiClient.get(`/api/excel/preview/${taskId}`, {
        params: { max_rows: 100000 } // 获取所有数据
      });

      if (response.data.success) {
        const { columns, rows } = response.data.data;

        // 找到打标结果列（后缀为 _labeled 的列）
        const labeledColumn = columns.find(col =>
          col.key.endsWith('_labeled') && col.type === 'enum'
        );

        if (!labeledColumn) {
          message.error('未找到打标结果列');
          setLoading(false);
          return;
        }

        // 统计标签分布
        const labelCounts = {};
        let totalLabeled = 0;

        rows.forEach(row => {
          const label = row[labeledColumn.key];
          // 过滤空值和无效值
          if (label && label !== '-' && label !== 'None' && label !== 'null' && label !== '') {
            labelCounts[label] = (labelCounts[label] || 0) + 1;
            totalLabeled++;
          }
        });

        // 转换为数组并按数量降序排序
        const sortedData = Object.entries(labelCounts)
          .map(([name, count]) => ({
            name,
            count,
            percentage: ((count / totalLabeled) * 100).toFixed(2)
          }))
          .sort((a, b) => b.count - a.count);

        setAnalysisData({
          data: sortedData,
          total: rows.length,
          labeled: totalLabeled,
          labeledColumn: labeledColumn.key
        });
      } else {
        message.error('获取数据失败');
      }
    } catch (error) {
      console.error('数据分析失败:', error);
      message.error('数据分析失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (visible && taskId) {
      fetchAndAnalyzeData();
    }
  }, [visible, taskId]); // eslint-disable-line react-hooks/exhaustive-deps

  // 表格列配置
  const tableColumns = [
    {
      title: '标签名称',
      dataIndex: 'name',
      key: 'name',
      width: '40%',
    },
    {
      title: '数量',
      dataIndex: 'count',
      key: 'count',
      width: '30%',
      sorter: (a, b) => a.count - b.count,
      defaultSortOrder: 'descend',
    },
    {
      title: '占比',
      dataIndex: 'percentage',
      key: 'percentage',
      width: '30%',
      render: (text) => `${text}%`,
    },
  ];

  // 柱状图配置
  const columnConfig = {
    data: analysisData?.data || [],
    xField: 'name',
    yField: 'count',
    label: {
      position: 'top',
      style: {
        fill: '#000000',
        opacity: 0.6,
      },
    },
    xAxis: {
      label: {
        autoHide: false,
        autoRotate: analysisData?.data?.length > 5,
      },
    },
    meta: {
      name: {
        alias: '标签',
      },
      count: {
        alias: '数量',
      },
    },
    columnStyle: {
      radius: [4, 4, 0, 0],
    },
    color: '#1890ff',
  };

  return (
    <Modal
      title="数据分析"
      open={visible}
      onCancel={onClose}
      footer={null}
      width={800}
      className="data-analysis-modal"
    >
      <Spin spinning={loading}>
        {analysisData && (
          <div className="analysis-content">
            {/* 统计摘要 */}
            <div className="stats-summary">
              <div className="stat-item">
                <Text type="secondary">总行数</Text>
                <Title level={3}>{analysisData.total}</Title>
              </div>
              <div className="stat-item">
                <Text type="secondary">已打标</Text>
                <Title level={3}>{analysisData.labeled}</Title>
              </div>
              <div className="stat-item">
                <Text type="secondary">打标率</Text>
                <Title level={3}>
                  {((analysisData.labeled / analysisData.total) * 100).toFixed(1)}%
                </Title>
              </div>
            </div>

            <Divider />

            {/* 表格 */}
            <div className="table-section">
              <Title level={4}>标签分布详情</Title>
              <Table
                columns={tableColumns}
                dataSource={analysisData.data}
                rowKey="name"
                pagination={false}
                size="middle"
                scroll={{ y: 240 }}
              />
            </div>

            <Divider />

            {/* 柱状图 */}
            <div className="chart-section">
              <Title level={4}>标签分布图</Title>
              <Column {...columnConfig} height={300} />
            </div>
          </div>
        )}
      </Spin>
    </Modal>
  );
};

export default DataAnalysisModal;
